import { useEffect, useRef, useState } from "react";

import Avatar from "../../../ui/avatar/Avatar";
import LeadPersonPicker, {
  moveListboxFocus,
  usePersonPickerActions,
} from "../LeadPersonPicker";
import QuickContactForm from "../../database/QuickContactForm";
import { extractApiErrorMessage } from "../../../utils/apiError";

import styles from "./CommunicationDetailPanel.module.css";

const ROLE_LABELS = {
  receptor: "Receptor",
  contacto: "Contacto",
  remitente: "Remitente",
  participante: "En el hilo",
};

const getDisplayName = (person) => person?.name || person?.email || "";

const isEmailOnlyRecipient = (person) => String(person?.id ?? "").startsWith("email:");

const needsContact = (person) => isEmailOnlyRecipient(person) && !person?.personId;

const isPersonOutsideLead = (person) =>
  isEmailOnlyRecipient(person) && Boolean(person?.personId);

const toLeadCandidate = (person, { id, name, position, photo }) => ({
  id,
  name: name || getDisplayName(person),
  email: person.email,
  position: position || "",
  photo: photo || null,
});

/**
 * Campo de destinatarios (Para o CC) con chips y desplegable de personas.
 *
 * Los destinatarios que todavía no son personas del lead (copiados por el
 * lead, remitentes desconocidos) muestran un "+" verde: si la dirección no
 * existe en Persons abre el alta rápida del contacto y, si existe pero no
 * está en el lead, la suma directo. En los dos casos el chip pasa a ser la
 * persona del lead.
 */
const RecipientField = ({
  label,
  value = [],
  options = [],
  excludedIds = [],
  onChange,
  disabled = false,
  personPicker = {},
  onAddPerson,
  marksRecipient = false,
  onMarkRecipient,
  contactLayer = null,
  lead = null,
}) => {
  const [open, setOpen] = useState(false);
  const [menuMode, setMenuMode] = useState("list");
  const [creatingEmail, setCreatingEmail] = useState(null);
  const [linkingEmail, setLinkingEmail] = useState(null);
  const [linkError, setLinkError] = useState(null);
  const menuContainerRef = useRef(null);
  const valueRef = useRef(value);
  valueRef.current = value;

  const addPersonToLead = onAddPerson
    ? (person) => onAddPerson(person, { asRecipient: marksRecipient })
    : undefined;

  const { saving, error, clearError, selectPerson } =
    usePersonPickerActions({
      onAddPerson: addPersonToLead,
      onAdded: (person) => {
        if (!person) return;
        onChange?.([...valueRef.current, person]);
        setMenuMode("list");
        setOpen(false);
      },
    });
  const canAddPersons = Boolean(onAddPerson);

  useEffect(() => {
    if (!open) return undefined;

    const handleClickOutside = (event) => {
      if (!menuContainerRef.current?.contains(event.target)) setOpen(false);
    };
    const handleKeyDown = (event) => {
      if (event.key === "Escape") setOpen(false);
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [open]);

  useEffect(() => {
    if (!creatingEmail) return undefined;

    const handleKeyDown = (event) => {
      if (event.key === "Escape") setCreatingEmail(null);
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [creatingEmail]);

  useEffect(() => {
    if (open) return;
    setMenuMode("list");
    clearError();
  }, [open, clearError]);

  const selectedIds = new Set(value.map((person) => String(person.id)));
  const unavailableIds = new Set(excludedIds.map((id) => String(id)));

  const handleAdd = (person) => {
    onChange?.([...value, person]);
    setOpen(false);

    if (marksRecipient && person.role === "contacto") onMarkRecipient?.(person);
  };

  const handleRemove = (person) => {
    onChange?.(
      value.filter((selected) => String(selected.id) !== String(person.id)),
    );
  };

  const replaceRecipient = (previousPerson, leadPerson) => {
    onChange?.(
      valueRef.current.map((selected) =>
        String(selected.id) === String(previousPerson.id) ? leadPerson : selected,
      ),
    );
  };

  const linkRecipientToLead = async (person, candidate) => {
    setLinkingEmail(person.email);
    setLinkError(null);

    try {
      const leadPerson = await addPersonToLead(candidate);
      if (leadPerson) replaceRecipient(person, leadPerson);
    } catch (addError) {
      setLinkError(
        extractApiErrorMessage(addError) || "No se pudo sumar la persona al lead.",
      );
    } finally {
      setLinkingEmail(null);
    }
  };

  const handleAddExistingPerson = (person) =>
    linkRecipientToLead(
      person,
      toLeadCandidate(person, {
        id: person.personId,
        name: person.name,
        position: person.position,
        photo: person.photo,
      }),
    );

  const handleContactCreated = (person, createdPerson) => {
    setCreatingEmail(null);
    if (!createdPerson?.id) return;

    linkRecipientToLead(person, toLeadCandidate(person, createdPerson));
  };

  return (
    <div className={styles.recipientField}>
      <span className={styles.recipientLabel}>{label}</span>

      <div className={styles.recipientChips}>
        {value.map((person) => {
          const displayName = getDisplayName(person);
          const canCreateContact =
            !disabled && canAddPersons && Boolean(contactLayer) && needsContact(person);
          const canAddToLead =
            !disabled && canAddPersons && isPersonOutsideLead(person);
          const isCreating = creatingEmail === person.email;
          const isLinking = linkingEmail === person.email;

          return (
            <span key={person.id} className={styles.recipientChipWrap}>
              <span
                className={`${styles.recipientChip} ${
                  needsContact(person) ? styles.recipientChipUnknown : ""
                }`}
                title={person.email || undefined}
              >
                <Avatar src={person.photo} name={displayName} size={18} />
                <span className={styles.recipientChipName}>{displayName}</span>
                {canCreateContact && (
                  <button
                    type="button"
                    className={styles.recipientChipLink}
                    title="Crear contacto"
                    aria-label={`Crear contacto para ${person.email}`}
                    aria-expanded={isCreating}
                    onClick={() => {
                      setLinkError(null);
                      setCreatingEmail(isCreating ? null : person.email);
                    }}
                  >
                    +
                  </button>
                )}
                {canAddToLead && (
                  <button
                    type="button"
                    className={styles.recipientChipLink}
                    title="Sumar al lead"
                    aria-label={`Sumar ${displayName} al lead`}
                    disabled={isLinking}
                    onClick={() => handleAddExistingPerson(person)}
                  >
                    +
                  </button>
                )}
                {!disabled && (
                  <button
                    type="button"
                    className={styles.recipientChipRemove}
                    onClick={() => handleRemove(person)}
                    aria-label={`Sacar ${displayName} de ${label}`}
                  >
                    ×
                  </button>
                )}
              </span>

              {isCreating && (
                <div
                  className={`${styles.recipientMenu} ${styles.recipientMenuPicker} ${styles.recipientChipForm}`}
                >
                  <QuickContactForm
                    entry={{
                      email: person.email,
                      name: displayName !== person.email ? displayName : "",
                    }}
                    account={contactLayer}
                    lead={lead}
                    onCancel={() => setCreatingEmail(null)}
                    onCreated={(createdPerson) =>
                      handleContactCreated(person, createdPerson)
                    }
                  />
                </div>
              )}
            </span>
          );
        })}

        <div className={styles.recipientAdd} ref={menuContainerRef}>
          <button
            type="button"
            className={styles.recipientAddButton}
            onClick={() => setOpen((previous) => !previous)}
            disabled={disabled}
            aria-haspopup="listbox"
            aria-expanded={open}
            aria-label={`Agregar a ${label}`}
          >
            + Agregar
          </button>

          {open && menuMode === "picker" && (
            <div
              className={`${styles.recipientMenu} ${styles.recipientMenuPicker}`}
            >
              <LeadPersonPicker
                {...personPicker}
                onSelect={selectPerson}
                onClose={() => {
                  setMenuMode("list");
                  clearError();
                }}
                saving={saving}
                error={error}
              />
            </div>
          )}

          {open && menuMode === "list" && (
            <div className={styles.recipientMenu}>
              {options.length === 0 ? (
                <div className={styles.recipientMenuEmpty}>
                  El lead no tiene personas cargadas.
                </div>
              ) : (
                <div
                  role="listbox"
                  aria-label={`Personas del lead para ${label}`}
                  onKeyDown={moveListboxFocus}
                >
                  {options.map((person) => {
                    const personId = String(person.id);
                    const isSelected = selectedIds.has(personId);
                    const isUnavailable =
                      isSelected || unavailableIds.has(personId) || !person.email;
                    const displayName = getDisplayName(person);
                    const roleLabel = ROLE_LABELS[person.role];

                    return (
                      <button
                        key={personId}
                        type="button"
                        role="option"
                        aria-selected={isSelected}
                        className={`${styles.recipientOption} ${
                          isUnavailable ? styles.recipientOptionDisabled : ""
                        }`}
                        disabled={isUnavailable}
                        onClick={() => handleAdd(person)}
                      >
                        <Avatar src={person.photo} name={displayName} size={24} />
                        <span className={styles.recipientOptionInfo}>
                          <span className={styles.recipientOptionName}>
                            {displayName}
                            {roleLabel && (
                              <span
                                className={`${styles.recipientRole} ${
                                  person.role === "receptor"
                                    ? styles.recipientRoleMain
                                    : ""
                                }`}
                              >
                                {roleLabel}
                              </span>
                            )}
                          </span>
                          <span className={styles.recipientOptionEmail}>
                            {person.email || "Sin email"}
                          </span>
                        </span>
                      </button>
                    );
                  })}
                </div>
              )}

              {canAddPersons && (
                <button
                  type="button"
                  className={styles.recipientMenuFooter}
                  onClick={() => setMenuMode("picker")}
                >
                  ＋ Agregar persona al lead
                </button>
              )}
            </div>
          )}
        </div>

        {linkError && (
          <p className={styles.recipientError} role="alert">
            {linkError}
          </p>
        )}
      </div>
    </div>
  );
};

export default RecipientField;
